import { motion } from "framer-motion";
import {
  Car,
  Cpu,
  Shirt,
  Hammer,
  FlaskConical,
  Cog,
} from "lucide-react";

const SECTORS = [
  {
    name: "Automotive Parts",
    sub: "& Components",
    icon: Car,
    testId: "sector-automotive",
  },
  {
    name: "Electronics",
    sub: "& Semiconductors",
    icon: Cpu,
    testId: "sector-electronics",
  },
  {
    name: "Apparel",
    sub: "& Textiles",
    icon: Shirt,
    testId: "sector-apparel",
  },
  {
    name: "Metals",
    sub: "& Fabricated Products",
    icon: Hammer,
    testId: "sector-metals",
  },
  {
    name: "Chemicals",
    sub: "& Industrials",
    icon: FlaskConical,
    testId: "sector-chemicals",
  },
  {
    name: "Machinery",
    sub: "& Equipment",
    icon: Cog,
    testId: "sector-machinery",
  },
];

// Marquee items
const TICKER = [
  "Automotive Parts",
  "Electronics & Semiconductors",
  "Apparel & Textiles",
  "Metals & Fabricated",
  "Chemicals & Industrials",
  "Machinery & Equipment",
  "Consumer Goods",
  "Furniture",
  "Toys",
  "Building Materials",
];

export default function Sectors() {
  return (
    <section
      id="sectors"
      data-testid="sectors-section"
      className="section-light relative py-24 sm:py-32 bg-white border-y border-[#0A1936]/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#0A1936]" />
            <span className="font-mono uppercase tracking-[0.24em] text-[11px] text-[#0A1936]/70">
              02 · Who This Affects
            </span>
          </div>
          <h2 className="font-serif text-[#0A1936] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            If you imported goods in 2025,
            <br />
            <span className="italic text-[#0A1936]/60">
              you&apos;re likely owed money.
            </span>
          </h2>
          <p className="mt-8 text-lg text-[#0A1936]/70 max-w-2xl leading-relaxed">
            Importers from{" "}
            <span className="font-semibold text-[#0A1936]">any country</span>{" "}
            subject to IEEPA reciprocal tariffs qualify. Six high-impact sectors
            dominate the recovery landscape.
          </p>
        </motion.div>

        <div
          data-testid="sectors-grid"
          className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-4"
        >
          {SECTORS.map((s, i) => (
            <motion.div
              key={s.name}
              data-testid={s.testId}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.07 }}
              whileHover={{ y: -6 }}
              className={`group relative overflow-hidden border border-[#0A1936]/10 hover:border-[#F5B800] bg-[#F9FAFB] hover:bg-[#0A1936] rounded-md p-8 sm:p-10 transition-all duration-500 cursor-default ${
                i === 1 || i === 4 ? "md:row-span-1 md:min-h-[280px]" : ""
              }`}
            >
              <div className="relative z-10 flex flex-col h-full">
                <s.icon
                  size={32}
                  className="text-[#0A1936] group-hover:text-[#F5B800] transition-colors"
                />
                <div className="flex-1" />
                <div className="mt-16">
                  <div className="font-serif text-2xl sm:text-3xl text-[#0A1936] group-hover:text-white transition-colors leading-tight">
                    {s.name}
                  </div>
                  <div className="mt-1 text-sm text-[#0A1936]/60 group-hover:text-white/70 transition-colors">
                    {s.sub}
                  </div>
                </div>
              </div>
              {/* animated gold underline */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#F5B800] scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-700" />
            </motion.div>
          ))}
        </div>

        {/* Marquee */}
        <div className="mt-16 overflow-hidden border-y border-[#0A1936]/10 py-6">
          <div className="flex whitespace-nowrap animate-marquee">
            {[...TICKER, ...TICKER].map((t, i) => (
              <span
                key={i}
                className="mx-8 font-serif italic text-2xl sm:text-3xl text-[#0A1936]/40 flex items-center gap-8"
              >
                {t}
                <span className="text-[#F5B800]">✦</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
